import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import dayjs from "dayjs";
import { FiUsers, FiCalendar, FiArrowLeft, FiUserX, FiCheck } from "react-icons/fi";
import { PiDog } from "react-icons/pi";
import "../../styles/OwnerDogGroups.css";

const OwnerDogGroups = ({ groups, dogId }) => {
    const { t } = useTranslation('groups');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [upcomingTrainings, setUpcomingTrainings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [absenceLoading, setAbsenceLoading] = useState(null);
    const [absenceNote, setAbsenceNote] = useState("");
    const [showNoteInput, setShowNoteInput] = useState(null);


    // Fetch upcoming trainings for a selected group
    const fetchUpcomingTrainings = async (groupId) => {
        if (!groupId) {
            console.error("Missing groupId for fetching trainings");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/group-trainings/group/${groupId}/upcoming`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Transform to add startTime/endTime for compatibility
            const trainings = (response.data || []).map(training => ({
                ...training,
                startTime: training.scheduledAt,
                endTime: dayjs(training.scheduledAt).add(training.durationMinutes || 60, 'minute').toISOString(),
            }));
            const sorted = [...trainings].sort((a, b) =>
                new Date(a.scheduledAt) - new Date(b.scheduledAt)
            );
            setUpcomingTrainings(sorted);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching upcoming trainings:", err);
            setError(t('fetchUpcomingTrainingsError'));
            setLoading(false);
        }
    };

    // Handle group selection
    const handleGroupSelect = (group) => {
        setSelectedGroup(group);
        fetchUpcomingTrainings(group.id);
    };

    // Go back to groups list
    const handleBackToGroups = () => {
        setSelectedGroup(null);
        setUpcomingTrainings([]);
    };

    // Get the dog's participation status for a training
    const getDogParticipation = (training) => {
        if (!training.dogParticipations || !dogId) return null;
        return training.dogParticipations.find(dp => dp.dogId === dogId);
    };

    // Mark dog as absent from a training
    const markAbsent = async (trainingId) => {
        setAbsenceLoading(trainingId);
        try {
            const token = localStorage.getItem("jwt");
            await axios.patch(
                `/api/v1/group-trainings/${trainingId}/dogs/${dogId}/absence/owner`,
                { note: absenceNote || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShowNoteInput(null);
            setAbsenceNote("");
            // Refresh trainings to get updated participation status
            if (selectedGroup) {
                fetchUpcomingTrainings(selectedGroup.id);
            }
        } catch (err) {
            console.error("Error marking absence:", err);
            setError(t('absenceError'));
        } finally {
            setAbsenceLoading(null);
        }
    };

    // Cancel absence (unmark)
    const cancelAbsence = async (trainingId) => {
        setAbsenceLoading(trainingId);
        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(
                `/api/v1/group-trainings/${trainingId}/dogs/${dogId}/absence`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (selectedGroup) {
                fetchUpcomingTrainings(selectedGroup.id);
            }
        } catch (err) {
            console.error("Error cancelling absence:", err);
            setError(t('absenceCancelError'));
        } finally {
            setAbsenceLoading(null);
        }
    };

    // Format date/time for display
    const formatDateTime = (dateTime) => {
        return dayjs(dateTime).format("DD.MM.YYYY HH:mm");
    };

    // Handle the case when groups data is null, undefined, or not an array
    if (!groups) {
        return (
            <div className="no-groups-message">
                <p>{t('loadingGroups')}</p>
            </div>
        );
    }

    // Ensure groups is an array
    const groupsArray = Array.isArray(groups) ? groups : [];

    if (groupsArray.length === 0) {
        return (
            <div className="no-groups-message">
                <p>{t('noGroupsAssigned')}</p>
            </div>
        );
    }

    // If a group is selected, show its details and upcoming trainings
    if (selectedGroup) {
        return (
            <div className="dog-group-details">
                <div className="group-details-header">
                    <button className="back-button" onClick={handleBackToGroups}>
                        <FiArrowLeft /> {t('backToGroups')}
                    </button>
                    <h2 className="group-name">{selectedGroup.name}</h2>
                </div>

                <div className="group-info-panel">
                    {selectedGroup.description && (
                    <div className="group-description">
                        {selectedGroup.description || t('noGroupDescription')}
                    </div>
                    )}
                    <div className="group-stats">
                        <div className="stat-item">
                            <FiUsers className="stat-icon" />
                            <span className="stat-label">{t('trainers')}:</span>
                            <span className="stat-value">{selectedGroup.trainersCount}</span>
                        </div>
                        <div className="stat-item">
                            <PiDog className="stat-icon" />
                            <span className="stat-label">{t('dogs')}:</span>
                            <span className="stat-value">{selectedGroup.dogsCount}</span>
                        </div>
                    </div>
                </div>

                <div className="upcoming-trainings-section">
                    <h3 className="section-title">
                        <FiCalendar className="section-icon" />
                        {t('upcomingTrainings')}
                    </h3>

                    {loading ? (
                        <div className="loading-trainings">
                            <p>{t('loadingTrainings')}</p>
                        </div>
                    ) : error ? (
                        <div className="error-message">
                            <p>{error}</p>
                        </div>
                    ) : upcomingTrainings.length === 0 ? (
                        <div className="no-trainings-message">
                            {t('noUpcomingGroupTrainings')}
                        </div>
                    ) : (
                        <div className="trainings-list">
                            {upcomingTrainings.map((training) => {
                                const participation = getDogParticipation(training);
                                const isAbsent = participation?.status === "ABSENT";
                                const isLoadingThis = absenceLoading === training.id;

                                return (
                                    <div key={training.id} className={`training-card ${isAbsent ? 'training-card-absent' : ''}`}>
                                        <div className="training-card-main">
                                            <div className="training-date">
                                                <FiCalendar className="date-icon" />
                                                {formatDateTime(training.startTime)}
                                            </div>
                                            <div className="training-details">
                                                {training.hallName && (
                                                    <div className="training-location">
                                                        <span className="detail-label">{t('hall')}:</span>
                                                        <span className="detail-value">{training.hallName}</span>
                                                    </div>
                                                )}
                                                <div className="training-duration">
                                                    <span className="detail-label">{t('duration')}:</span>
                                                    <span className="detail-value">
                                                        {dayjs(training.startTime).format("HH:mm")} -
                                                        {dayjs(training.endTime).format("HH:mm")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="training-absence-section">
                                            {isAbsent ? (
                                                <div className="absence-status">
                                                    <span className="absence-badge">{t('markedAbsent')}</span>
                                                    <button
                                                        className="cancel-absence-btn"
                                                        onClick={() => cancelAbsence(training.id)}
                                                        disabled={isLoadingThis}
                                                    >
                                                        <FiCheck />
                                                        <span>{t('cancelAbsence')}</span>
                                                    </button>
                                                </div>
                                            ) : showNoteInput === training.id ? (
                                                <div className="absence-note-form">
                                                    <input
                                                        type="text"
                                                        className="absence-note-input"
                                                        placeholder={t('absenceNotePlaceholder')}
                                                        value={absenceNote}
                                                        onChange={(e) => setAbsenceNote(e.target.value)}
                                                    />
                                                    <div className="absence-note-actions">
                                                        <button
                                                            className="confirm-absence-btn"
                                                            onClick={() => markAbsent(training.id)}
                                                            disabled={isLoadingThis}
                                                        >
                                                            {isLoadingThis ? t('loading') : t('confirmAbsence')}
                                                        </button>
                                                        <button
                                                            className="cancel-note-btn"
                                                            onClick={() => { setShowNoteInput(null); setAbsenceNote(""); }}
                                                        >
                                                            {t('cancel')}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    className="mark-absent-btn"
                                                    onClick={() => setShowNoteInput(training.id)}
                                                    disabled={isLoadingThis}
                                                >
                                                    <FiUserX />
                                                    <span>{t('markAbsent')}</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Default view: List of groups
    return (
        <div className="dog-groups-container">
            <div className="groups-list">
                {groupsArray.map((group) => (
                    <div
                        key={group.id}
                        className="group-card"
                        onClick={() => handleGroupSelect(group)}
                    >
                        <h3 className="group-card-title">{group.name}</h3>
                        <div className="group-card-stats">
                            <div className="group-card-stat">
                                <FiUsers className="stat-icon" />
                                <span>{group.trainersCount} {t('trainers')}</span>
                            </div>
                            <div className="group-card-stat">
                                <PiDog className="stat-icon small" />
                                <span>{group.dogsCount} {t('dogs')}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OwnerDogGroups;
